import queryString from 'query-string';

import { QUOTE_PART_VIEWER_ROUTE_PREFIX } from 'modules/quotePartViewer/constants';
import { partViewerRoutePrefix } from 'modules/viewerPage/data/routes';

/**
 * Returns window features string for pop-out viewer
 */
export const getPopOutViewerWindowFeatures = (): string => {
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const width = Math.floor(screenWidth * 0.9);
  const height = Math.floor(screenHeight * 0.9);
  return `width=${width},height=${height},menubar=yes,toolbar=yes,location=yes`;
};

export type PopOutViewerUrlParams = {
  activePartUuid?: string;
  isMaterialWidgetSelected?: boolean;
  partMasterUuid?: string;
  partMasterVersion?: number | string;
  quoteItemId?: number | string;
  quoteNumberRevisionNumber?: string;
  selectedFileUuid?: string;
  appsPanelState?: string;
  partSetupToolState?: number;
};

/**
 * Builds the URL for the pop-out viewer based on the provided parameters
 */
export const buildPopOutViewerUrl = ({
  activePartUuid,
  isMaterialWidgetSelected,
  partMasterUuid,
  partMasterVersion,
  quoteItemId,
  quoteNumberRevisionNumber,
  selectedFileUuid,
  appsPanelState,
  partSetupToolState,
}: PopOutViewerUrlParams): string => {
  const queryParams = queryString.stringify({
    active_part: activePartUuid || undefined,
    part_setup: partSetupToolState || undefined,
    selected_file: selectedFileUuid || undefined,
    quote_item_id: quoteItemId || undefined,
    material_selected: isMaterialWidgetSelected || undefined,
    app: appsPanelState || undefined, // determines which panel to open to in the viewer
  });

  if (quoteNumberRevisionNumber) {
    return `${QUOTE_PART_VIEWER_ROUTE_PREFIX}/${quoteNumberRevisionNumber}?${queryParams}`;
  }

  return `${partViewerRoutePrefix}/${partMasterUuid}?version=${partMasterVersion}${
    queryParams ? `&${queryParams}` : ''
  }`;
};

export const openPopOutViewerFunc = ({
  activePartUuid,
  isMaterialWidgetSelected,
  partMasterUuid,
  partMasterVersion,
  quoteItemId,
  quoteNumberRevisionNumber,
  selectedFileUuid,
  appsPanelState,
  partSetupToolState,
}: PopOutViewerUrlParams) => {
  const url = buildPopOutViewerUrl({
    activePartUuid,
    isMaterialWidgetSelected,
    partMasterUuid,
    partMasterVersion,
    quoteItemId,
    quoteNumberRevisionNumber,
    selectedFileUuid,
    appsPanelState,
    partSetupToolState,
  });
  const windowFeatures = getPopOutViewerWindowFeatures();
  window.open(url, 'popout', windowFeatures);
};

type CreatePartFilePopOutViewerLinkParam = {
  quoteNumberRevisionNumber?: string;
  quoteItemId: number | string;
  isRootComponent: boolean;
  partUuid: string;
  selectedFileUuid?: string;
  partMasterUuid: string;
  partMasterVersion: number | string;
};

export const createPartFilePopOutViewerLink = ({
  quoteNumberRevisionNumber,
  quoteItemId,
  isRootComponent,
  partUuid,
  selectedFileUuid,
  partMasterUuid,
  partMasterVersion,
}: CreatePartFilePopOutViewerLinkParam) => {
  const inputs: PopOutViewerUrlParams = {
    quoteNumberRevisionNumber,
    quoteItemId,
    selectedFileUuid,
    partMasterUuid,
    partMasterVersion,
  };
  if (!isRootComponent) {
    inputs.activePartUuid = partUuid;
  }
  if (selectedFileUuid) {
    inputs.selectedFileUuid = selectedFileUuid;
  }

  return buildPopOutViewerUrl(inputs);
};
